import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import { getPosters } from '../../Store/posters/postersSlice';
import styles from '../../styles/HomePage.module.scss';
import BG from '../../images/shlifmashina.webp';
import { posterUrl } from '../../utils/imageUrl';
import CarouselNav from '../common/CarouselNav';
import ProductImage from '../common/ProductImage';
import { getProductUrl } from '../../utils/urls';

const DESKTOP_QUERY = '(min-width: 1025px)';
const AUTOPLAY_MS = 5000;

const PosterSlide = ({ poster, onButtonClick, isLcp = false }) => {
  const title = poster.title;
  const subtitle = poster.subtitle;
  const heading = poster.heading;
  const buttonText = poster.buttonText;
  const initialImageUrl = useMemo(
    () => ((poster.image && typeof poster.image === 'string') ? posterUrl(poster.image) : BG),
    [poster.image]
  );
  const [imageUrl, setImageUrl] = useState(initialImageUrl);

  useEffect(() => {
    setImageUrl(initialImageUrl);
  }, [initialImageUrl]);

  return (
    <div className={styles.home}>
      <div className={styles.title}>{title}</div>
      <div className={styles.product}>
        <div className={styles.image}>
          <ProductImage
            src={imageUrl}
            alt={heading || 'Постер'}
            width={400}
            height={300}
            loading={isLcp ? 'eager' : 'lazy'}
            fetchPriority={isLcp ? 'high' : undefined}
            onError={() => setImageUrl(BG)}
          />
        </div>
        <div className={styles.subtitle}>{subtitle}</div>
        <div className={styles.head} role="heading" aria-level="2">{heading}</div>
        <button
          type="button"
          className={styles.button}
          onClick={() => onButtonClick(poster)}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const Poster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );
  const { list: posters } = useSelector((state) => state.posters);

  useEffect(() => {
    dispatch(getPosters());
  }, [dispatch]);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const syncLayout = () => setIsDesktop(media.matches);
    syncLayout();
    media.addEventListener('change', syncLayout);
    return () => media.removeEventListener('change', syncLayout);
  }, []);

  useEffect(() => {
    if (isDesktop || !posters || posters.length <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setSlideIndex((current) => (current + 1) % posters.length);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [isDesktop, posters]);

  useEffect(() => {
    if (!posters?.length) {
      return;
    }
    setSlideIndex((current) => (current >= posters.length ? 0 : current));
  }, [posters]);

  const handleButtonClick = (poster) => {
    if (poster.productId) {
      navigate(getProductUrl({ id: poster.productId, slug: poster.productSlug }));
    } else {
      navigate(poster.buttonLink || '/');
    }
  };

  const defaultPoster = {
    title: 'Велика знижка 20%',
    subtitle: 'Розпродаж',
    heading: 'Стрічкова шліфувальна машина APRO SB1100',
    buttonText: 'Купити зараз',
    image: null,
    productId: null,
    buttonLink: '/',
  };

  const posterList = posters?.length ? posters : [defaultPoster];
  const hasMultipleSlides = posterList.length > 1;
  const activePoster = posterList[slideIndex] ?? posterList[0];

  const goToPrev = () => {
    setSlideIndex((current) => (current - 1 + posterList.length) % posterList.length);
  };

  const goToNext = () => {
    setSlideIndex((current) => (current + 1) % posterList.length);
  };

  const posterNav = hasMultipleSlides ? (
    <CarouselNav
      total={posterList.length}
      current={slideIndex}
      compactThreshold={5}
      onPrev={isDesktop ? () => sliderRef.current?.slickPrev() : goToPrev}
      onNext={isDesktop ? () => sliderRef.current?.slickNext() : goToNext}
      onDotClick={isDesktop
        ? (index) => sliderRef.current?.slickGoTo(index)
        : setSlideIndex}
    />
  ) : null;

  if (!isDesktop) {
    return (
      <section className={styles.posterCarousel} data-poster-carousel>
        <div className={styles.posterFrame}>
          <PosterSlide
            key={activePoster.id ?? slideIndex}
            poster={activePoster}
            onButtonClick={handleButtonClick}
            isLcp={slideIndex === 0}
          />
        </div>
        {posterNav}
      </section>
    );
  }

  if (!hasMultipleSlides) {
    return (
      <section className={styles.posterCarousel} data-poster-carousel>
        <PosterSlide poster={posterList[0]} onButtonClick={handleButtonClick} isLcp />
      </section>
    );
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: AUTOPLAY_MS,
    fade: true,
    arrows: false,
    pauseOnHover: true,
    beforeChange: (_current, next) => setSlideIndex(next),
  };

  return (
    <section className={styles.posterCarousel} data-poster-carousel>
      <Slider ref={sliderRef} {...settings}>
        {posterList.map((poster, index) => (
          <div key={poster.id ?? index} className={styles.slide}>
            <PosterSlide
              poster={poster}
              onButtonClick={handleButtonClick}
              isLcp={index === 0}
            />
          </div>
        ))}
      </Slider>
      {posterNav}
    </section>
  );
};

export default Poster;
